import { E as EVENTS } from './events-58bc9098.js';
import { AlgoliaClient, publishAlgoliaEvent } from './algolia-client.js';
import { formatMoney, imageUrlWithWidth } from './algolia-product-tile.js';

function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[c]);
}

const PRODUCT_SRCSET_WIDTHS = [160, 240, 320, 400];

// Renders one product hit to match snippets/product-item-minimal.liquid's
// markup/classes exactly (whole_item_linked, show_price, show_vendor) so
// the existing component-product-item-minimal.css needs no changes.
//
// `searchContext` ({ queryID, indexName }) mirrors algolia-product-tile.js's
// renderProductTile - without it, a click here never carried the queryID to
// the PDP at all (tag 357 only ever reads it off these ?algolia_qid=... URL
// params), so this specific path's purchase attribution had nothing to rely
// on except the old single-slot sessionStorage/cookie mechanism
// (persistQueryID) and the now-paused legacy "purchase w/ Query"/"w/o Query"
// tags that read it - a gap the collection/search grid never had.
function renderProductResult(hit, config, index, searchContext) {
  let productUrl = `${config.rootUrl.replace(/\/$/, "")}/products/${hit.handle}`;
  if (searchContext?.queryID) {
    // algolia_pos carries the result's 1-based position so the GTM link-click
    // tag ("Algolia - clicked (link)") can send it to clickedObjectIDsAfterSearch
    // for click-through-rate-by-position analytics.
    productUrl += `?${new URLSearchParams({
      algolia_qid: searchContext.queryID,
      algolia_idx: searchContext.indexName || "",
      algolia_oid: hit.objectID,
      algolia_pos: String(index + 1),
    })}`;
  }
  const priceVaries = hit.variants_min_price !== hit.variants_max_price;
  const priceHtml = priceVaries
    ? config.strings.priceRangeHtml.replace("[[price]]", formatMoney(hit.variants_min_price))
    : formatMoney(hit.price);

  const image = hit.image || hit.product_image;
  const srcset = image ? PRODUCT_SRCSET_WIDTHS.map((w) => `${imageUrlWithWidth(image, w)} ${w}w`).join(", ") : "";
  // escapeHtml(hit.title) here, not the whole label below - viewProduct is
  // Liquid `t`-filter output (already HTML-escaped); double-escaping it
  // would mangle it the moment its locale string ever needs a literal
  // quote/ampersand around the placeholder (see updateSearchButton for the
  // exact failure mode this avoids).
  const viewProductLabel = config.strings.viewProduct.replace("[[product]]", escapeHtml(hit.title));
  const vendorHtml = config.showVendor
    ? `<p class="product-item-minimal__vendor fs-body-60">${escapeHtml(hit.vendor)}</p>`
    : "";

  return `
    <a
      href="${escapeHtml(productUrl)}"
      class="product-item-minimal product-item-minimal--size-standard"
      aria-label="${viewProductLabel}"
      data-algolia-objectid="${escapeHtml(hit.objectID)}"
      data-algolia-position="${index + 1}"
    >
      <div class="product-item-minimal__image">
        <img
          class="predictive-search-product-image"
          src="${escapeHtml(imageUrlWithWidth(image, 240))}"
          srcset="${srcset}"
          sizes="(min-width: 1024px) 102px, (min-width: 720px) 80px, 80px"
          loading="lazy"
          alt="${escapeHtml(hit.title)}"
        >
      </div>

      <div class="product-item-minimal__content">
        <div class="product-item-minimal__content-main">
          <div class="product-item-minimal__content-main-left">
            ${vendorHtml}
            <h3 class="product-item-minimal__title fs-body-100">${escapeHtml(hit.title)}</h3>
            <p class="product-item-minimal__price fs-body-100">${priceHtml}</p>
          </div>
        </div>
      </div>
    </a>
  `;
}

// Collections/articles render as plain text links, same as Shopify's native
// predictive search markup for these two categories (see
// sections/predictive-search.liquid) - no images/insights tracking, just a
// jump-off point.
//
// hit.url is used when the Algolia record provides it directly; constructing
// the path from handle alone is only attempted as a fallback (see
// buildFallbackUrl callers - articles need blog.handle, nested, not a flat
// blog_handle field, confirmed against a live record). A hit with neither
// renders as plain (non-clickable) text instead of silently vanishing from
// the list, same as algolia-collection-grid.js's renderSecondaryResults -
// dropping it entirely makes a whole result category look empty even
// though Algolia found hits.
function renderSecondaryResult(hit, type, buildFallbackUrl) {
  const url = hit.url || buildFallbackUrl(hit);
  const titleHtml = escapeHtml(hit.title);

  if (!url) {
    // Logging the raw hit (not just title/objectID) so the actual record
    // shape is visible in DevTools - it's the only way to see what field
    // this store's Algolia articles index really uses, since queries
    // against Algolia's own API aren't reachable from here either.
    console.warn(
      `[predictive-search-drawer] ${type} hit "${hit.title}" (objectID ${hit.objectID}) has no usable url/handle field - check the record shape in Algolia and adjust renderSecondaryResult.`,
      hit,
    );
    return `<li><span>${titleHtml}</span></li>`;
  }

  return `<li><a href="${escapeHtml(url)}">${titleHtml}</a></li>`;
}

class PredictiveSearchDrawer extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      shopifySection: ".shopify-section",
      resultsContainer: ".predictive-search-drawer_results-container",
      searchInput: "search-input",
      searchButton: ".predictive-search-drawer__submit-button",
      footer: ".predictive-search-drawer__footer-content",
      configScript: "[js-algolia-predictive-config]",
      viewAllTemplate: "[js-view-all-button-template]",
    };

    this.states = {
      results: "results",
      default: "default",
      empty: "empty",
      loading: "loading",
    };
  }

  connectedCallback() {
    this.dialog = this.closest(this.selectors.dialogParent);
    this.shopifySection = this.closest(this.selectors.shopifySection);
    this.searchInput = this.querySelector(this.selectors.searchInput);
    this.resultsContainer = this.querySelector(this.selectors.resultsContainer);
    this.sectionId = this.dataset.sectionId;
    this.eventController = new AbortController();
    this.queryFetchController = new AbortController();
    this.queryFetchPending = false;

    this.searchTerm = "";
    this.searchButton = this.querySelector(this.selectors.searchButton);
    this.footer = this.querySelector(this.selectors.footer);
    this.viewAllTemplate = this.querySelector(this.selectors.viewAllTemplate);

    this.searchConfig = {
      maxResultsPerCategory: parseInt(this.dataset.maxResultsPerCategory, 10),
    };

    const configEl = this.querySelector(this.selectors.configScript);
    this.config = configEl ? JSON.parse(configEl.textContent) : null;

    this.cachedResults = {};
    this.currentQueryID = null;
    this.currentIndexName = null;

    if (this.config?.appId && this.config?.searchKey && this.config?.indexName) {
      this.client = new AlgoliaClient({ appId: this.config.appId, searchKey: this.config.searchKey });
    } else {
      console.warn("[predictive-search-drawer] missing Algolia app ID/search key/index name - search disabled.");
    }

    if (window?.Shopify?.designMode) {
      this.#bindThemeEditorEvents();
    }

    window.addEventListener(
      EVENTS.PREDICTIVE_SEARCH_DRAWER_OPEN,
      () => {
        this.searchInput.focus();
      },
      { signal: this.eventController.signal },
    );

    // The Insights "clicked" event still rides GTM's "Algolia - result link
    // click" trigger (Click - Just Links, Wait for Tags), reading the
    // ?algolia_qid= params renderProductResult puts on each link - unaffected by
    // the handler below.
    //
    // Separately, seed the attribution pixel's click store on click-through so
    // an add-to-cart on the destination PDP carries this queryID to the Stape
    // store (server purchasedObjectIDsAfterSearch). Best-effort: the publish
    // races navigation, but the pixel's localStorage click store survives it.
    // The sandboxed pixel can't read the ?algolia_qid= param or sessionStorage
    // itself, so this publish is the only bridge. Product results carry
    // data-algolia-objectid; secondary results (collections/articles/pages)
    // don't, so they no-op here.
    this.addEventListener(
      "click",
      (event) => {
        if (!event.target.closest("a[href]")) return;
        const tile = event.target.closest("[data-algolia-objectid]");
        if (!tile || !this.currentQueryID) return;
        // Reuse publishAlgoliaEvent (already imported) rather than a new export,
        // so this entry gains no new cross-file import. The pixel subscribes to
        // "algolia_result_add_intent" and writes its click store.
        publishAlgoliaEvent("algolia_result_add_intent", {
          objectID: String(tile.dataset.algoliaObjectid),
          queryID: this.currentQueryID,
          index: tile.dataset.algoliaPosition != null ? String(tile.dataset.algoliaPosition) : null,
        });
      },
      { signal: this.eventController.signal },
    );

    this.addEventListener(EVENTS.SEARCH_INPUT_CHANGED, this.onChange);
  }

  #bindThemeEditorEvents() {
    document.addEventListener(
      EVENTS.SHOPIFY_SECTION_SELECT,
      (event) => {
        if (event.target === this.shopifySection) {
          window.dispatchEvent(new Event(EVENTS.PREDICTIVE_SEARCH_DRAWER_OPEN));
        }
      },
      { signal: this.eventController.signal },
    );

    document.addEventListener(
      EVENTS.SHOPIFY_SECTION_DESELECT,
      (event) => {
        if (event.target === this.shopifySection) {
          window.dispatchEvent(new Event(EVENTS.PREDICTIVE_SEARCH_DRAWER_CLOSE));
          this.openedViaThemeEditorEvents = false;
        }
      },
      { signal: this.eventController.signal },
    );
  }

  setDisplayedState(state) {
    this.dataset.displayedState = state;
  }

  getQuery() {
    return this.searchInput.getValue()
  }

  clearResults() {
    this.resultsContainer.innerHTML = "";
    this.setDisplayedState(this.states.default);
  }

  // cacheEntry.queryID/indexName are restored here too - without this, a
  // cache hit (e.g. typing "abc", "abcd", then deleting back to "abc") would
  // leave currentQueryID pointing at whatever query ran most recently rather
  // than the one that actually produced the results now on screen, so a
  // click straight after would tag the wrong queryID.
  renderSearchResults(cacheEntry) {
    this.currentQueryID = cacheEntry.queryID;
    this.currentIndexName = cacheEntry.indexName;

    this.resultsContainer.replaceChildren(cacheEntry.markup);

    this.updateSearchButton(cacheEntry.markup.dataset.viewAllUrl, cacheEntry.markup.dataset.viewAllLabel);
    if (cacheEntry.markup.dataset.totalResultCount === "0") {
      this.setDisplayedState(this.states.empty);
    } else {
      this.setDisplayedState(this.states.results);
    }
  }

  // viewAllLabel already went through Liquid's `t` filter, which HTML-escapes
  // its own output (including the literal quote marks around the search
  // term) - only the raw searchTerm substituted into it client-side needs
  // escaping (done once, where it's inserted - see buildResultsMarkup),
  // so it must NOT be escaped again here or the quotes come out
  // double-encoded ("&amp;quot;" -> renders as literal "&quot;" text).
  updateSearchButton(viewAllUrl, viewAllLabel) {
    if (!this.viewAllTemplate || !viewAllUrl) return;

    // Set the href on the parsed anchor via the DOM instead of substituting the
    // URL into the template's raw markup. The template now ships a valid absolute
    // fallback (href="/search"), NOT a "[[VIEW_ALL_URL]]" placeholder, because
    // Google's URL discovery scans raw HTML for href= patterns even inside an
    // inert <template> - a relative placeholder there gets crawled as
    // "<page>/[[VIEW_ALL_URL]]" on every page (it's injected globally via the
    // header). Setting href through setAttribute also removes any need to
    // HTML-escape the URL into an attribute string.
    const html = this.viewAllTemplate.innerHTML
      .replaceAll("[[VIEW_ALL_LABEL]]", viewAllLabel);

    const button = new DOMParser().parseFromString(html, "text/html").querySelector(".btn-wrapper");
    if (!button) return;

    const link = button.querySelector("a");
    if (link) link.setAttribute("href", viewAllUrl);

    this.footer.replaceChildren(button);
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.clearResults();
    }
    this.setDisplayedState(this.states.loading);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.clearResults();
      return
    }

    this.getSearchResults(this.searchTerm);
  }

  buildResultsMarkup(searchTerm, productResult, collectionResult, articleResult, pageResult) {
    const productHits = productResult?.hits || [];
    const collectionHits = collectionResult?.hits || [];
    const articleHits = articleResult?.hits || [];
    const pageHits = pageResult?.hits || [];
    const totalResultCount = productHits.length + collectionHits.length + articleHits.length + pageHits.length;
    const searchContext = productResult?.queryID
      ? { queryID: productResult.queryID, indexName: this.config.indexName }
      : undefined;

    const root = document.createElement("div");
    root.className = "predictive-search-results";
    root.setAttribute("role", "listbox");
    root.dataset.totalResultCount = String(totalResultCount);
    root.dataset.viewAllUrl = `${this.config.searchUrl}?q=${encodeURIComponent(searchTerm)}`;
    // escapeHtml here, once - viewAllLabelTemplate is Liquid `t`-filter
    // output (already HTML-escaped, including the literal quotes around the
    // placeholder), so this is the only point the raw searchTerm needs it.
    root.dataset.viewAllLabel = this.config.strings.viewAllLabelTemplate.replace("[[QUERY]]", escapeHtml(searchTerm));

    if (totalResultCount === 0) {
      root.innerHTML = `
        <div class="predictive-search__empty-state">
          <p class="fs-body-150">${this.config.strings.noResultsTitleTemplate.replace("[[TERMS]]", escapeHtml(searchTerm))}</p>
          <p class="t-subdued">${this.config.strings.noResultsParagraph}</p>
        </div>
      `;
      return root;
    }

    const section = document.createElement("section");

    const productsBlock =
      productHits.length > 0
        ? `
        <div class="predictive-search__result-set predictive-search__result-set--type-products">
          <h2 class="predictive-search__result-set__heading ff-overline fs-overline">${escapeHtml(this.config.strings.productsHeading)}</h2>
          <ul class="predictive-search__result-set__results">
            ${productHits.map((hit, i) => `<li>${renderProductResult(hit, this.config, i, searchContext)}</li>`).join("")}
          </ul>
        </div>
      `
        : "";

    const collectionsBlock =
      collectionHits.length > 0
        ? `
        <div class="predictive-search__result-set predictive-search__result-set--type-collections">
          <h2 class="predictive-search__result-set__heading ff-overline fs-overline">${escapeHtml(this.config.strings.collectionsHeading)}</h2>
          <ul class="predictive-search__result-set__results predictive-search__result-set__results--text-items">
            ${collectionHits
              .map((hit) =>
                renderSecondaryResult(
                  hit,
                  "collection",
                  (h) => (h.handle ? `${this.config.rootUrl.replace(/\/$/, "")}/collections/${h.handle}` : null),
                ),
              )
              .join("")}
          </ul>
        </div>
      `
        : "";

    const pagesBlock =
      pageHits.length > 0
        ? `
        <div class="predictive-search__result-set predictive-search__result-set--type-pages">
          <h2 class="predictive-search__result-set__heading ff-overline fs-overline">${escapeHtml(this.config.strings.pagesHeading || "Pages")}</h2>
          <ul class="predictive-search__result-set__results predictive-search__result-set__results--text-items">
            ${pageHits
              .map((hit) =>
                renderSecondaryResult(
                  hit,
                  "page",
                  (h) => (h.handle ? `${this.config.rootUrl.replace(/\/$/, "")}/pages/${h.handle}` : null),
                ),
              )
              .join("")}
          </ul>
        </div>
      `
        : "";

    const articlesBlock =
      articleHits.length > 0
        ? `
        <div class="predictive-search__result-set predictive-search__result-set--type-articles">
          <h2 class="predictive-search__result-set__heading ff-overline fs-overline">${escapeHtml(this.config.strings.articlesHeading)}</h2>
          <ul class="predictive-search__result-set__results predictive-search__result-set__results--text-items">
            ${articleHits
              .map((hit) =>
                renderSecondaryResult(
                  hit,
                  "article",
                  (h) =>
                    h.handle && h.blog?.handle
                      ? `${this.config.rootUrl.replace(/\/$/, "")}/blogs/${h.blog.handle}/${h.handle}`
                      : null,
                ),
              )
              .join("")}
          </ul>
        </div>
      `
        : "";

    // Collections and Pages are both short text lists, so they share a row on
    // desktop (.predictive-search__result-row pairs them 2-up; see
    // component-predictive-search-drawer.css) and stack collections -> pages on
    // mobile. Articles have longer titles, so they stay full-width at the bottom.
    const secondaryRow =
      collectionsBlock || pagesBlock
        ? `<div class="predictive-search__result-row">${collectionsBlock}${pagesBlock}</div>`
        : "";

    section.innerHTML = productsBlock + secondaryRow + articlesBlock;

    root.appendChild(section);
    return root;
  }

  // Fired once per rendered result set, same as the collection grid's
  // runQuery - Algolia's Search Analytics (CTR/conversion/no-results rate)
  // is computed from the ...AfterSearch event family, which needs a
  // "viewed" event tagged with this query's queryID as its first step.
  reportViewed(productResult) {
    const hits = productResult?.hits || [];
    if (!hits.length || !productResult.queryID) return;

    publishAlgoliaEvent("algolia_result_viewed", {
      index: this.config.indexName,
      queryID: productResult.queryID,
      objectIDs: hits.map((h) => h.objectID),
      positions: hits.map((_, i) => i + 1),
    });
  }

  async getSearchResults(searchTerm) {
    if (!this.client) return;

    if (this.queryFetchPending) {
      // No custom reason: fetch() rejects with signal.reason verbatim when
      // one is given (a plain string here, previously), not a DOMException
      // - which broke the error?.name === "AbortError" check below and
      // turned every superseded query into a logged "query failed". The
      // default (no reason) reliably produces a real AbortError instead.
      this.queryFetchController.abort();
      this.queryFetchPending = false;
      this.queryFetchController = new AbortController();
    }

    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return
    }

    const requests = [
      {
        indexName: this.config.indexName,
        params: {
          query: searchTerm,
          hitsPerPage: this.searchConfig.maxResultsPerCategory,
          page: 0,
          clickAnalytics: true,
        },
      },
    ];
    const collectionsRequestIndex = this.config.collectionsIndexName
      ? requests.push({
          indexName: this.config.collectionsIndexName,
          params: { query: searchTerm, hitsPerPage: this.config.maxCollectionResults || 3, page: 0 },
        }) - 1
      : null;
    const articlesRequestIndex = this.config.articlesIndexName
      ? requests.push({
          indexName: this.config.articlesIndexName,
          params: { query: searchTerm, hitsPerPage: this.config.maxArticleResults || 3, page: 0 },
        }) - 1
      : null;
    // Pages (shopify_pages) - brand pages, service pages, info pages. Surfaced
    // as a neutral "Pages" set (the index has no brand marker to filter by),
    // so a brand-name search returns its page alongside relevant service pages.
    const pagesRequestIndex = this.config.pagesIndexName
      ? requests.push({
          indexName: this.config.pagesIndexName,
          params: { query: searchTerm, hitsPerPage: this.config.maxPageResults || 3, page: 0 },
        }) - 1
      : null;

    this.queryFetchPending = true;
    this.searchInput.setInProgress(true);

    try {
      const results = await this.client.multiQuery(requests, { signal: this.queryFetchController.signal });
      this.queryFetchPending = false;
      this.searchInput.setInProgress(false);

      const productResult = results[0];
      const collectionResult = collectionsRequestIndex !== null ? results[collectionsRequestIndex] : null;
      const articleResult = articlesRequestIndex !== null ? results[articlesRequestIndex] : null;
      const pageResult = pagesRequestIndex !== null ? results[pagesRequestIndex] : null;

      const cacheEntry = {
        markup: this.buildResultsMarkup(searchTerm, productResult, collectionResult, articleResult, pageResult),
        queryID: productResult.queryID,
        indexName: this.config.indexName,
      };
      this.cachedResults[queryKey] = cacheEntry;
      this.renderSearchResults(cacheEntry);

      this.reportViewed(productResult);
    } catch (error) {
      this.queryFetchPending = false;
      this.searchInput.setInProgress(false);
      if (error?.name === "AbortError") {
        // Superseded by a newer keystroke's query - not a real failure.
        return
      }
      console.error("[predictive-search-drawer] query failed", error);
    }
  }

  disconnectedCallback() {
    this.eventController.abort();
    this.queryFetchController.abort();
  }
}

if (!customElements.get("predictive-search-drawer")) {
  customElements.define("predictive-search-drawer", PredictiveSearchDrawer);
}